/**
 * @file caseLabelService.js
 * @description Case LABELS for a signed-in provider's own relationships.
 *
 *   A provider's "My applications", Inbox, offers and invites need a few facts
 *   about a case (agency display name, type, setting, pay, ZIP, case code, paused
 *   flag). They used to get them through `case:cases(...)` embeds, which only
 *   worked because the `cases` table was readable by every signed-in user. The
 *   table is now closed to everyone but the owning agency and admins, so the
 *   provider side reads the same facts through get_case_labels, a SECURITY
 *   DEFINER function that returns an allowlist and enforces the relationship:
 *   a case the caller applied to (any status) or a live case.
 *
 *   Agency screens do NOT use this: an agency reads its own cases directly.
 *
 *   Kept in its own small module (supabase only) so the application, message and
 *   interview services can use it without pulling in the rest of caseService.
 * @module lib/caseLabelService
 */
import { supabase } from "./supabase";

// get_case_labels caps one call at 200 ids.
const CHUNK = 200;

/**
 * @description Fetches case label rows by id.
 * @param {string[]} ids - case ids (duplicates and nulls are ignored)
 * @returns {Promise<Object<string,Object>>} map of case id -> label row. Ids the
 *   caller may not see are simply absent. {} on error (labels are decoration).
 */
export async function loadCaseLabels(ids) {
  const unique = [...new Set((ids || []).filter(Boolean))];
  if (unique.length === 0) return {};
  const out = {};
  for (let i = 0; i < unique.length; i += CHUNK) {
    const { data, error } = await supabase.rpc("get_case_labels", { p_case_ids: unique.slice(i, i + CHUNK) });
    if (error) return out;
    for (const row of (data || [])) out[row.id] = row;
  }
  return out;
}

/**
 * @description The six case facts the dismiss-signal engine reads (pay floor,
 *   setting, coordinates, client age band) for a provider's passed cases, through
 *   get_case_dismiss_signals. Replaces the `cases ( ... )` embed on saved_cases.
 * @param {string[]} ids - case ids
 * @returns {Promise<Object<string,Object>>} map of case id -> { id, pay_min, setting, lat, lng, age_min, age_max }
 */
export async function loadCaseDismissSignals(ids) {
  const unique = [...new Set((ids || []).filter(Boolean))];
  if (unique.length === 0) return {};
  const out = {};
  for (let i = 0; i < unique.length; i += CHUNK) {
    const { data, error } = await supabase.rpc("get_case_dismiss_signals", { p_case_ids: unique.slice(i, i + CHUNK) });
    if (error) throw error;
    for (const row of (data || [])) out[row.id] = row;
  }
  return out;
}

/**
 * @description Re-attaches label rows to rows that carry a `case_id`, under the
 *   key the old embed used, so every downstream mapper is untouched.
 * @param {Object[]} rows - rows with a case_id
 * @param {string} [key="case"] - property to set ("case" for applications, "cases" for invites)
 * @param {string} [idKey="case_id"] - property holding the case id
 * @returns {Promise<Object[]>} new rows, each with row[key] = label row | null
 */
export async function attachCaseLabels(rows, key = "case", idKey = "case_id") {
  const list = rows || [];
  const labels = await loadCaseLabels(list.map(r => r[idKey]));
  return list.map(r => ({ ...r, [key]: labels[r[idKey]] || null }));
}
